import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import CompanyService, {
  DataType,
  Entities
} from "../../../services/CompanyService";
import InvoiceService from "../../../services/InvoiceService";
import {
  formatCurrency,
  formatCurrencyConsolidated,
  formatDateShort,
  formatNumber,
  unbreak
} from "../../../utils/FormatUtils";
import { TranslationService } from "../../../services/TranslationService";
import TableContext from "../../task/TableContext";
import ClientDetailContext from "../ClientDetailContext";
import { InvoiceList } from "./entities/InvoiceListResponse";
import { InvoiceChangeStatusForm } from "./InvoiceChangeStatusForm";
import FloatingPanelService from "../../shared/FloatingPanel";
import InvoiceDetail from "./InvoiceDetail";
import ModalService, { ModalParams } from "../../shared/bootstrap/modal/Modal";
import { InvoiceEditStatus } from "./entities/InvoiceEditStatus";
import { ToastService } from "../../shared/bootstrap/Toast";
import { TableHeaderWithFieldId } from "../../shared/Table";
import { Cast, OptionalMap } from "../../../utils/Utils";
import { getError, handleErrorWithToast } from "../../../utils/RequestUtils";
import { CacheEntity, StorageService } from "../../../services/StorageService";
import { percentFormatCollection } from "../../../utils/PercentCollection";
import {
  parseDate,
  restTimezone,
  MapIfHasHours,
  hasTimeComponent
} from "../../../utils/ParseUtils";
import Select from "../../shared/components/Select";
import { Tooltip } from "bootstrap";
import "./invoiceStyles.css";

const InvoiceTableItem = (
  onCheckboxChange?: (
    invoiceId: number,
    checked: boolean,
    invoiceData?: InvoiceList.Item
  ) => void,
  headers?: TableHeaderWithFieldId[],
  reloadDataList?: () => void
) =>
  function InvoiceTableItemImpl({ data }: { data: InvoiceList.Item }) {
    const specialCases = {
      download: () =>
        !data.HasFiles ? (
          <td></td>
        ) : (
          <td>
            <i
              className="fas fa-fw fa-download pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleErrorWithToast(
                  InvoiceService.downloadAllFilesFromInvoice(data.IOID)
                );
              }}
            />
          </td>
        ),

      group: () => {
        const text =
          data.Group?.Name ??
          (data.GroupID
            ? String(
                StorageService.getCache(CacheEntity.GroupName, data.GroupID) ??
                  ""
              )
            : "");

        return (
          <td className={`th-w-160 th-truncate`}>
            <span
              style={{ display: "inline-block", maxWidth: "100%" }}
              onMouseEnter={(e) => showTooltip(e.currentTarget)}
              onMouseLeave={(e) => Tooltip.getInstance(e.currentTarget)?.hide()}
            >
              {text}
            </span>
          </td>
        );
      }
    };

    return (
      <ClientDetailContext.Consumer>
        {({ invoiceIds, reloadExtraData }) => (
          <TableContext.Consumer>
            {({ reload }) => {
              if (!headers) {
                headers = InvoiceService.getTableHeaders();
              }
              return (
                <tr
                  key={data.IOID}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    FloatingPanelService.showPanel({
                      children: (
                        <InvoiceDetail
                          key={data.IOID}
                          invoiceId={data.IOID}
                          isConsolidated
                          reload={() => {
                            reloadDataList && reloadDataList();
                            reload();
                            reloadExtraData();
                          }}
                        />
                      ),
                      title: "",
                      width: 750,
                      height: 800
                    })
                  }
                >
                  {onCheckboxChange && (
                    <td
                      onClick={(event) => event.stopPropagation()}
                      className="p-0"
                    >
                      <div className="fixed-checkbox-left">
                        <input
                          type="checkbox"
                          className="checkbox-invoice"
                          data-invoiceselected={data.IOID}
                          checked={invoiceIds.includes(data.IOID)}
                          onChange={(event) =>
                            onCheckboxChange(
                              data.IOID,
                              (event.target as HTMLInputElement).checked,
                              data
                            )
                          }
                        />
                      </div>
                    </td>
                  )}
                  {headers
                    .filter((x) => x.fieldId !== "checkbox")
                    .map((header) => (
                      <TableColumn
                        key={header.fieldId}
                        data={data}
                        header={header}
                        specialCases={specialCases}
                      />
                    ))}
                </tr>
              );
            }}
          </TableContext.Consumer>
        )}
      </ClientDetailContext.Consumer>
    );
  };

function showTooltip(elem: HTMLElement) {
  const isTruncated = elem.scrollWidth > elem.clientWidth;
  const text = (elem.textContent || "").trim();

  const tip = Tooltip.getOrCreateInstance(elem, {
    trigger: "manual",
    container: "body",
    customClass: "tooltipIntiza"
  });

  if (isTruncated && text) {
    elem.setAttribute("data-bs-original-title", text);
    (tip as any).setContent?.({ ".tooltip-inner": text });

    tip.show();
  } else {
    tip.hide();
    tip.dispose();
    elem.removeAttribute("data-bs-original-title");
  }
}

export const TableColumn = <T,>({
  data,
  header,
  specialCases = {}
}: {
  data: InvoiceList.Item;
  header: TableHeaderWithFieldId;
  specialCases?: {
    [key: string]: undefined | ((x: T) => undefined | React.ReactElement);
  };
}) => {
  const { translate, currentLanguage } = TranslationService;
  const { reload } = useContext(TableContext);
  const { reloadExtraData } = useContext(ClientDetailContext);

  // Obtener clase CSS de ancho para campos adicionales con Width definido
  const getCellWidthClass = (): string => {
    if (header.key.startsWith("ioadditional.")) {
      const definition = CompanyService.getAdditionalDefinitions().find(
        (x) => x.AdditionalDefinitionID.toString() === header.fieldId
      );
      if (definition?.Width === 160) {
        return " th-w-160";
      } else if (definition?.Width === 240) {
        return " th-w-240";
      }
    }
    return "";
  };

  const cellWidthClass = getCellWidthClass();
  const hasFixedWidth = cellWidthClass !== "";
  const baseTdClass =
    `${header.numeric ? "text-end" : ""} ${header.className || ""}${cellWidthClass}`.trim();

  const getValue = () => {
    if (header.key.startsWith("ioadditional.")) {
      return (
        data.additionals.find((x) => x.Id.toString() === header.fieldId)
          ?.Value ??
        Cast<{ transactionadditionals?: typeof data.additionals }>(
          data
        ).transactionadditionals?.find(
          (x) => x.Id.toString() === header.fieldId
        )?.Value
      );
    }
    return data[header.fieldId as keyof InvoiceList.Item];
  };

  const statusFields = useMemo(() => {
    return new Map(
      CompanyService.getIoStatus().map((status) => {
        return [
          status.IOStatusID,
          CompanyService.getAdditionalDefinitionsFiltered(
            Entities.Invoice
          ).filter(
            (x) =>
              x.IOStatusDependencies.filter(
                (y) => y.IOStatusID === status.IOStatusID
              ).length > 0
          )
        ];
      })
    );
  }, []);

  const negativeValueClass = CompanyService.getSetting("iosnegativeamountinred")
    ? "text-danger"
    : "";
  const value = getValue();
  const specialCase = specialCases[header.fieldId];
  if (specialCase) {
    const caseResult = specialCase(Cast<T>(data));
    if (caseResult) {
      return caseResult;
    }
  }

  switch (header.fieldId) {
    case "StatusIcon":
      return (
        <td className={`${baseTdClass} th-center`}>
          {data.pending === 0 ? <i className="far fa-square-check"></i> : ""}
        </td>
      );

    case "dueDateDays":
      if (data.dueDays === 0) {
        return (
          <td className={`${baseTdClass} th-w-160 th-nowrap`}>
            {data.dueDate ? formatDateShort(new Date(data.dueDate)) : ""}
          </td>
        );
      }
      if (data.dueDays > 0) {
        return (
          <td className={`${baseTdClass} th-w-160 th-nowrap text-danger`}>
            {formatDateShort(new Date(data.dueDate))}
            <small className="ps-1">
              ({data.dueDays} {translate.Days})
            </small>
          </td>
        );
      }
      return (
        <td className={`${baseTdClass} th-w-160 th-nowrap`}>
          {unbreak(formatDateShort(new Date(data.dueDate)))}
        </td>
      );

    case "dueDate":
      if (data.dueDays === 0) {
        return (
          <td className={`${baseTdClass} th-w-160 th-truncate`}>
            {data.dueDate ? formatDateShort(new Date(data.dueDate)) : ""}
          </td>
        );
      }
      if (data.dueDays > 0) {
        return (
          <td className={`${baseTdClass} th-w-160 th-truncate text-danger`}>
            {formatDateShort(new Date(data.dueDate))}
          </td>
        );
      }
      return (
        <td className={`${baseTdClass} th-w-160 th-truncate`}>
          {unbreak(formatDateShort(new Date(data.dueDate)))}
        </td>
      );

    case "status": {
      const status =
        CompanyService.getIoStatus().find(
          (x) => x.IOStatusID === data.IOStatusID
        )?.Value ?? CompanyService.getNormalIOStatusName();

      if (
        CompanyService.canDo("changeiostatus") &&
        CompanyService.getConfigItems().find((x) => x.field === -9)?.ManualEdit
      ) {
        const onStatusSubmit = async (statusEdit: InvoiceEditStatus) => {
          const result = await InvoiceService.statusChange({
            invoiceIds: [data.IOID],
            personId: data.personId.toString(),
            statusEdit
          });
          if (result instanceof Error) {
            const errorDetected = getError(result);
            if (
              errorDetected?.message &&
              errorDetected.message === "AdditionalDependancy"
            ) {
              ToastService.showToast(
                translate.ErrorProcessingRequest,
                undefined,
                "danger"
              );
            } else {
              ToastService.showToast(
                translate.ErrorProcessingRequest,
                undefined,
                "danger"
              );
            }
          } else {
            ToastService.showToast(
              translate.SavedChanges,
              undefined,
              "success"
            );
            if (result.refreshactivity) {
              reloadExtraData();
            }
          }
          ModalService.hideModal();
          reload();
        };

        const statusAction = (statusId: number) => {
          if (statusFields.get(statusId)?.length) {
            const changeStatusModal = new ModalParams();
            changeStatusModal.children = (
              <InvoiceChangeStatusForm
                onSubmit={onStatusSubmit}
                personId={data.personId.toString()}
                defaultStatusId={statusId}
                defaultAdditionals={data.additionals}
              />
            );
            changeStatusModal.title = translate.ChangeIOStatus;
            changeStatusModal.closeClickingOutside = true;
            return () => ModalService.showModal(changeStatusModal);
          } else {
            return () =>
              onStatusSubmit({
                additionals: [],
                statusId: statusId,
                userId: CompanyService.getUserid()
              });
          }
        };

        const changeStatusModal = new ModalParams();
        changeStatusModal.children = (
          <InvoiceChangeStatusForm
            onSubmit={onStatusSubmit}
            personId={data.personId.toString()}
            defaultStatusId={data.IOStatusID ?? 0}
            defaultAdditionals={data.additionals}
          />
        );

        changeStatusModal.title = translate.ChangeIOStatus;
        changeStatusModal.closeClickingOutside = true;
        const allowedStatuses = CompanyService.getAllowedInvoiceStatuses();
        const statuses = allowedStatuses.map((x) => ({
          text: x.Value,
          value: statusAction(x.IOStatusID)
        }));

        // Si el estado actual no está en los permitidos, agregarlo a las opciones para mostrarlo
        const currentStatusId = data.IOStatusID ?? 0;
        const currentStatusInAllowed = allowedStatuses.find(
          (s) => s.IOStatusID === currentStatusId
        );
        let currentStatusOption = statuses.find(
          (x) => x.text === status
        )?.value;

        if (!currentStatusInAllowed) {
          // El estado actual no está permitido, pero debemos mostrarlo
          const allStatuses = CompanyService.getIoStatus();
          const currentStatusObj = allStatuses.find(
            (s) => s.IOStatusID === currentStatusId
          );
          if (currentStatusObj) {
            // Agregar el estado actual a las opciones aunque no esté permitido
            statuses.unshift({
              text: currentStatusObj.Value,
              value: statusAction(currentStatusId)
            });
            currentStatusOption = statuses[0].value;
          }
        }

        return (
          <td
            className={`${baseTdClass} th-truncate`}
            onClick={(e) => e.stopPropagation()}
          >
            <Select
              options={statuses}
              onChange={(x) => x()}
              extraClass="invoice-table-select"
              value={currentStatusOption}
            />
          </td>
        );
      }
      return (
        <td className={`${baseTdClass} th-truncate`}>{unbreak(status)}</td>
      );
    }

    case "percentDue": {
      return (
        <td className={`${baseTdClass}`}>
          {percentFormatCollection(data.overdueAmount, data.amount)}
        </td>
      );
    }

    case "pending2": {
      const defaultCurrencyId = CompanyService.getDefaultCurrencyId();
      if (data.CurrencyID === defaultCurrencyId) {
        return <td className={`${baseTdClass} text-end`}></td>;
      }
      const currencies = CompanyService.getCurrencies();
      const rate = currencies.find(
        (x) => x.CurrencyId === data.CurrencyID
      )!.Rate;
      return (
        <td
          className={`${baseTdClass} text-end${data.pending < 0 ? " " + negativeValueClass : ""}`}
        >
          {formatCurrency(data.pending * rate, defaultCurrencyId)}
        </td>
      );
    }

    case "Name":
      const companyName =
        (data as any)?.CompanyName ??
        (data as any)?.Person?.Name ??
        (data as any)?.personName ??
        (data as any)?.PersonName ??
        Cast<{ Fullname?: string }>(data).Fullname ??
        (data as any)?.Name ??
        (data as any)?.personKey ??
        "";
      return (
        <td
          className={`long-text ${baseTdClass} th-min-200 th-max-480 th-truncate`}
        >
          <Link
            to={`/${currentLanguage}/consolidated/client/details?id=${data.personId}`}
          >
            {companyName}
          </Link>
        </td>
      );
  }

  if (value === undefined || value === null || value === "") {
    return <td className={baseTdClass}></td>;
  }

  switch (header.type) {
    case DataType.Currency:
      return (
        <td
          className={`${baseTdClass} th-truncate text-end${parseFloat(value) < 0 ? " " + negativeValueClass : ""}`}
        >
          <span
            onMouseEnter={(e) => showTooltip(e.currentTarget)}
            onMouseLeave={(e) => Tooltip.getInstance(e.currentTarget)?.hide()}
          >
            {formatCurrencyConsolidated(parseFloat(value), data.CurrencySymbol)}
          </span>
        </td>
      );

    case DataType.Number:
      return (
        <td className={`${baseTdClass} th-truncate text-end`}>
          {formatNumber(value)}
        </td>
      );

    case DataType.Date:
      // Verificar el string original antes de parsearlo para determinar si tiene componente de hora
      const valueStr =
        value !== undefined && value !== null ? String(value) : undefined;
      const hasTime = valueStr ? hasTimeComponent(valueStr) : false;
      const dateWidthClass = hasFixedWidth ? "" : " th-w-160";
      return (
        <td className={`${baseTdClass}${dateWidthClass} th-nowrap`}>
          {OptionalMap(parseDate(valueStr), (x) => {
            if (!hasTime) {
              // Fecha sin hora: mostrar directamente sin ajuste de huso horario
              return formatDateShort(x);
            }
            // Fecha con hora: aplicar restTimezone solo si el objeto Date tiene hora
            return formatDateShort(MapIfHasHours(x, restTimezone));
          })}
        </td>
      );

    case DataType.List: {
      const additionalId = header.fieldId;
      const additionalDefinition =
        CompanyService.getAdditionalDefinitions().find(
          (x) => x.AdditionalDefinitionID.toString() === additionalId
        );
      const definition = additionalDefinition?.AdditionalDefinitionItems.find(
        (x) => x.AdditionalDefinitionItemID.toString() === value
      );
      return <td className={baseTdClass}>{definition?.Value}</td>;
    }

    default:
      const defaultWidthClass = hasFixedWidth ? "" : " th-w-160";
      return (
        <td className={`${baseTdClass}${defaultWidthClass} th-truncate`}>
          <span
            onMouseEnter={(e) => showTooltip(e.currentTarget)}
            onMouseLeave={(e) => Tooltip.getInstance(e.currentTarget)?.hide()}
          >
            {value}
          </span>
        </td>
      );
  }
};

export default InvoiceTableItem;
